"use client"

import { FieldArray, Formik, Form, useFormikContext, type FormikErrors } from "formik"
import { Loader2, Plus, Trash2 } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useSaveParamAliasSetsMutation } from "@/hooks/mutations/attribution-param-aliases"
import {
  DEFAULT_URL_PARAM_ALIAS_SETS,
  type UrlParamAliasSet,
  type UtmTargetField,
} from "@/lib/attribution/url-param-aliases"
import { useTranslations } from "next-intl"



// Self-contained editor: Apply persists the aliases on their own (server action), independent
// of the channels save. `usingDefaults` renders the platform presets read-to-edit; storing it
// keeps NULL in the DB so the tenant follows future preset updates.

const UTM_TARGET_FIELDS: UtmTargetField[] = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
]

interface AliasFormValues {
  usingDefaults: boolean
  sets: UrlParamAliasSet[]
}

interface UrlParamAliasesDialogProps {
  integrationId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  value: UrlParamAliasSet[] | null
  onSaved: (next: UrlParamAliasSet[] | null) => void
}

export function UrlParamAliasesDialog({
  integrationId,
  open,
  onOpenChange,
  value,
  onSaved,
}: UrlParamAliasesDialogProps) {

  const tUi = useTranslations("Shopify.ShopifyAttributionDashboardDialogsUrlParamAliasesDialog")
  const tCommonUi = useTranslations("Common")

  const saveMutation = useSaveParamAliasSetsMutation()

  const initialValues: AliasFormValues = {
    usingDefaults: value === null,
    sets: (value ?? DEFAULT_URL_PARAM_ALIAS_SETS).map(migrateLegacyImplied),
  }

  const handleSubmit = async (values: AliasFormValues) => {
    const paramAliasSets = values.usingDefaults ? null : sanitizeAliasSetsForSave(values.sets)

    try {
      const saved = await saveMutation.mutateAsync({ integrationId, paramAliasSets })
      onSaved(saved.paramAliasSets)
      onOpenChange(false)
    } catch {
      // withQueryHandling already surfaced the error toast; keep the dialog open.
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <Formik initialValues={initialValues} enableReinitialize validate={validateAliasForm} onSubmit={handleSubmit}>
          {({ values, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span><span key={tUi("uRLParameterAliases")}>{tUi("uRLParameterAliases")}</span></span>
                  {values.usingDefaults ? (
                    <Badge variant="secondary"><span key={tUi("platformDefaults")}>{tUi("platformDefaults")}</span></Badge>
                  ) : (
                    <Badge variant="outline"><span key={tUi("custom")}>{tUi("custom")}</span></Badge>
                  )}
                </DialogTitle>
                <DialogDescription>
                  <span><span key={tUi("mapNonStandardTrackingParamsFromLandingPage")}>{tUi("mapNonStandardTrackingParamsFromLandingPage")}</span></span> <code>tw_source</code><span><span key={tUi("ontoEmptyUTMFieldsBeforeChannelMatchingAliases")}>{tUi("ontoEmptyUTMFieldsBeforeChannelMatchingAliases")}</span></span>
                </DialogDescription>
              </DialogHeader>

              <FieldArray name="sets">
                {({ push, remove }) => (
                  <div className="space-y-4 py-4">
                    {values.sets.map((set, setIndex) => (
                      <AliasSetCard
                        key={setIndex}
                        set={set}
                        setIndex={setIndex}
                        disabled={isSubmitting}
                        onRemove={() => remove(setIndex)}
                      />
                    ))}

                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={isSubmitting}
                      onClick={() => {
                        void setFieldValue("usingDefaults", false)
                        push({ name: `Alias set ${values.sets.length + 1}`, rules: [{ param: "", mapsTo: "utm_source" }] })
                      }}
                    >
                      <Plus className="mr-2 h-4 w-4" /> <span><span key={tUi("addAliasSet")}>{tUi("addAliasSet")}</span></span>
                    </Button>
                  </div>
                )}
              </FieldArray>

              <DialogFooter>
                <div className="flex w-full items-center justify-between gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={values.usingDefaults || isSubmitting}
                    onClick={() => {
                      void setFieldValue("usingDefaults", true)
                      void setFieldValue("sets", DEFAULT_URL_PARAM_ALIAS_SETS)
                    }}
                  >
                    <span key={tUi("restorePlatformDefaults")}>{tUi("restorePlatformDefaults")}</span></Button>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" disabled={isSubmitting} onClick={() => onOpenChange(false)}>
                      <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> <span key={tUi("saving")}>{tUi("saving")}</span></>
                      ) : (
                        tUi("apply")
                      )}
                    </Button>
                  </div>
                </div>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

interface AliasSetCardProps {
  set: UrlParamAliasSet
  setIndex: number
  disabled: boolean
  onRemove: () => void
}

// Dense rule rows bind through Formik helpers rather than components/form-inputs: those render
// a visible label per field, which does not fit a param → target row.
function AliasSetCard({ set, setIndex, disabled, onRemove }: AliasSetCardProps) {
  const tUi = useTranslations("Shopify.ShopifyAttributionDashboardDialogsUrlParamAliasesDialog")

  const { getFieldProps, setFieldValue, errors } = useFormikContext<AliasFormValues>()
  const setErrors = Array.isArray(errors.sets) ? (errors.sets[setIndex] as AliasSetErrors | undefined) : undefined

  const markCustom = () => setFieldValue("usingDefaults", false)

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex items-start gap-2">
        <div className="space-y-1">
          <Input
            placeholder={tUi("setName")}
            className="max-w-56"
            disabled={disabled}
            aria-invalid={Boolean(setErrors?.name)}
            {...getFieldProps(`sets.${setIndex}.name`)}
            onChange={(event) => {
              void markCustom()
              getFieldProps(`sets.${setIndex}.name`).onChange(event)
            }}
          />
          {setErrors?.name && <p className="text-xs text-destructive">{setErrors.name}</p>}
        </div>
        <div className="flex-1" />
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label={tUi("removeAliasSet")}
          disabled={disabled}
          onClick={() => { void markCustom(); onRemove() }}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <FieldArray name={`sets.${setIndex}.rules`}>
        {({ push, remove }) => (
          <div className="space-y-2">
            {set.rules.map((rule, ruleIndex) => {
              const paramName = `sets.${setIndex}.rules.${ruleIndex}.param`
              const paramError = setErrors?.rules?.[ruleIndex]?.param
              return (
                <div key={ruleIndex} className="flex items-start gap-2">
                  <div className="space-y-1">
                    <Input
                      placeholder={tUi("uRLParamEGTwSource")}
                      className="max-w-56"
                      disabled={disabled}
                      aria-invalid={Boolean(paramError)}
                      {...getFieldProps(paramName)}
                      onChange={(event) => {
                        void markCustom()
                        getFieldProps(paramName).onChange(event)
                      }}
                    />
                    {paramError && <p className="text-xs text-destructive">{paramError}</p>}
                  </div>
                  <span className="pt-2 text-xs text-muted-foreground">→</span>
                  <Select
                    value={rule.mapsTo}
                    disabled={disabled}
                    onValueChange={(nextValue) => {
                      void markCustom()
                      void setFieldValue(`sets.${setIndex}.rules.${ruleIndex}.mapsTo`, nextValue)
                    }}
                  >
                    <SelectTrigger className="w-44">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {UTM_TARGET_FIELDS.map((field) => (
                        <SelectItem key={field} value={field}>
                          {field}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="space-y-1">
                    <Input
                      placeholder={tUi("fallbackOptional")}
                      className="max-w-40"
                      disabled={disabled}
                      aria-invalid={Boolean(setErrors?.rules?.[ruleIndex]?.fallback)}
                      {...getFieldProps(`sets.${setIndex}.rules.${ruleIndex}.fallback`)}
                      value={rule.fallback ?? ""}
                      onChange={(event) => {
                        void markCustom()
                        void setFieldValue(`sets.${setIndex}.rules.${ruleIndex}.fallback`, event.target.value)
                      }}
                    />
                    {setErrors?.rules?.[ruleIndex]?.fallback && (
                      <p className="text-xs text-destructive">{setErrors.rules[ruleIndex]?.fallback}</p>
                    )}
                  </div>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    aria-label={tUi("removeRule")}
                    disabled={disabled}
                    onClick={() => { void markCustom(); remove(ruleIndex) }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )
            })}
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={() => {
                void markCustom()
                push({ param: "", mapsTo: "utm_source" })
              }}
            >
              <Plus className="mr-2 h-4 w-4" /> <span><span key={tUi("addRule")}>{tUi("addRule")}</span></span>
            </Button>
          </div>
        )}
      </FieldArray>

      <p className="text-xs text-muted-foreground">
        <span><span key={tUi("fallbackIsUsedWhenItsParamIsMissing")}>{tUi("fallbackIsUsedWhenItsParamIsMissing")}</span></span> <code>tw_medium</code> <span>→</span> <code>cpc</code><span><span key={tUi("valuesParsedByShopifyAndParamsFoundBy")}>{tUi("valuesParsedByShopifyAndParamsFoundBy")}</span></span>
      </p>
    </div>
  )
}

interface AliasSetErrors {
  name?: string
  rules?: ({ param?: string; fallback?: string } | undefined)[]
}

// Stored sets may carry the legacy set-level `implied`; surface it as the fallback of the rule
// that targets the same field so editing writes the current shape.
function migrateLegacyImplied(set: UrlParamAliasSet): UrlParamAliasSet {
  if (!set.implied) return set

  const remaining = { ...set.implied }
  const rules = set.rules.map((rule) => {
    const legacy = remaining[rule.mapsTo]
    if (!legacy || rule.fallback) return rule
    delete remaining[rule.mapsTo]
    return { ...rule, fallback: legacy }
  })

  return { name: set.name, rules, ...(Object.keys(remaining).length > 0 ? { implied: remaining } : {}) }
}

// Mirrors aliasSetSchema for the fields a user can break; blank params are dropped on save,
// so they are not flagged while typing.
function validateAliasForm(values: AliasFormValues): FormikErrors<AliasFormValues> {
  const setErrors: (AliasSetErrors | undefined)[] = values.sets.map((set) => {
    const name = set.name.trim().length > 64 ? "Use 64 characters or fewer." : undefined

    const ruleErrors = set.rules.map((rule) => {
      const param = rule.param.trim()
      const fallback = (rule.fallback ?? "").trim()
      const fallbackError = fallback.length > 256 ? "Use 256 characters or fewer." : undefined

      if (param && /[=&\s]/.test(param)) return { param: "No '=', '&' or spaces.", ...(fallbackError ? { fallback: fallbackError } : {}) }
      if (param.length > 64) return { param: "Use 64 characters or fewer.", ...(fallbackError ? { fallback: fallbackError } : {}) }
      return fallbackError ? { fallback: fallbackError } : undefined
    })

    const hasRuleError = ruleErrors.some(Boolean)
    if (!name && !hasRuleError) return undefined
    return { ...(name ? { name } : {}), ...(hasRuleError ? { rules: ruleErrors } : {}) }
  })

  return setErrors.some(Boolean) ? ({ sets: setErrors } as FormikErrors<AliasFormValues>) : {}
}

// Trims values and drops incomplete rows for the save payload; keeps `null` (= defaults) as-is.
export function sanitizeAliasSetsForSave(sets: UrlParamAliasSet[] | null): UrlParamAliasSet[] | null {
  if (sets === null) return null

  return sets
    .map((set, index) => {
      const rules = set.rules
        .map((rule) => {
          const fallback = rule.fallback?.trim()
          return { param: rule.param.trim(), mapsTo: rule.mapsTo, ...(fallback ? { fallback } : {}) }
        })
        .filter((rule) => rule.param.length > 0)

      const impliedEntries = Object.entries(set.implied ?? {})
        .map(([field, impliedValue]) => [field, impliedValue?.trim() ?? ""] as const)
        .filter(([, impliedValue]) => impliedValue.length > 0)

      return {
        name: set.name.trim() || `Alias set ${index + 1}`,
        rules,
        implied: impliedEntries.length > 0 ? Object.fromEntries(impliedEntries) : undefined,
      }
    })
    .filter((set) => set.rules.length > 0)
}
